import { z } from 'zod';

/**
 * Conversion attribution: where a member arrived from, captured at the landing
 * boundary and attached only to designated conversion events (ADR-132).
 *
 * One record carries two families of values. The Iterable campaign/template
 * pair names a marketing send; the acquisition fields (the standard UTM
 * params, an experiment arm, and an opaque per-link reference) name a
 * campaign that may have no Iterable send behind it at all, such as a mailer.
 * Every value is a short bounded token. Never the URL, never the push
 * payload, never anything that could identify the member.
 */

/** A captured pair is live for this long after `capturedAt`, then discarded. */
export const CONVERSION_ATTRIBUTION_WINDOW_HOURS = 24;

/** Landing-URL query parameters Iterable stamps on campaign links. */
export const CONVERSION_ATTRIBUTION_URL_PARAMS = {
  campaignId: 'utm_itbl_campaign_id',
  templateId: 'utm_itbl_template_id',
} as const;

/**
 * Push payload keys. Iterable nests its ids under an `itbl` block; a
 * Galapagos-authored push may carry the same two ids at the top level.
 */
export const CONVERSION_ATTRIBUTION_PUSH_KEYS = {
  block: 'itbl',
  campaignId: 'campaignId',
  templateId: 'templateId',
} as const;

/** The acquisition fields of a record, in their canonical order. */
export const CONVERSION_ATTRIBUTION_ACQUISITION_FIELDS = [
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmContent',
  'utmTerm',
  'experimentArm',
  'linkRef',
] as const;
export type ConversionAttributionAcquisitionField =
  (typeof CONVERSION_ATTRIBUTION_ACQUISITION_FIELDS)[number];

/**
 * Landing-URL query parameters for the acquisition fields, keyed by record
 * field. Read from a landing URL or a deep link exactly like the Iterable
 * pair; any other capture source that receives the same keys (an install
 * provider's first-open params, say) reads them through the same evaluator.
 */
export const CONVERSION_ATTRIBUTION_ACQUISITION_PARAMS = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmContent: 'utm_content',
  utmTerm: 'utm_term',
  experimentArm: 'exp_arm',
  linkRef: 'link_ref',
} as const satisfies Record<ConversionAttributionAcquisitionField, string>;

/**
 * Where a record came from. The single extension point for a new capture
 * surface: a source joins by adding its value here and calling the params
 * evaluator with it, never by minting a second record or storage key.
 * `install` is the first-open parameters of an attributed app install (a
 * deferred deep link), evaluated exactly like a landing URL's query.
 */
export const CONVERSION_ATTRIBUTION_SOURCES = ['url', 'push', 'install'] as const;
export const conversionAttributionSourceSchema = z.enum(CONVERSION_ATTRIBUTION_SOURCES);
export type ConversionAttributionSource = z.infer<typeof conversionAttributionSourceSchema>;

/**
 * Opaque vendor id grammar. Iterable ids are numeric today; the grammar
 * admits the wider alphanumeric shape but nothing that could smuggle an
 * email, a path, or a token through the attribution slot.
 */
export const conversionAttributionIdSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]{1,64}$/, 'Expected an opaque vendor id: 1-64 of [A-Za-z0-9_-]');

/**
 * A UTM value. Campaign labels in practice (`directmail`, `fa_nov26`,
 * `google.com`). The grammar admits no `@`, `/`, whitespace, `%`, `?`, `=`,
 * `+`, or `:`, so an email, a path, a URL, or free text cannot pass, and the
 * length cap keeps credential-sized strings out.
 */
export const conversionAttributionUtmValueSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9._-]{1,100}$/, 'Expected a campaign label: 1-100 of [A-Za-z0-9._-]');

/** An experiment arm label (`control`, `treatment_b`). */
export const conversionAttributionExperimentArmSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]{1,32}$/, 'Expected an arm label: 1-32 of [A-Za-z0-9_-]');

/**
 * Per-field schemas for the acquisition values. The link reference is an
 * opaque id minted for the mailer, so it shares the vendor id grammar.
 */
export const CONVERSION_ATTRIBUTION_ACQUISITION_VALUE_SCHEMAS = {
  utmSource: conversionAttributionUtmValueSchema,
  utmMedium: conversionAttributionUtmValueSchema,
  utmCampaign: conversionAttributionUtmValueSchema,
  utmContent: conversionAttributionUtmValueSchema,
  utmTerm: conversionAttributionUtmValueSchema,
  experimentArm: conversionAttributionExperimentArmSchema,
  linkRef: conversionAttributionIdSchema,
} as const satisfies Record<ConversionAttributionAcquisitionField, z.ZodType<string>>;

const VALUE_FIELDS = [
  'campaignId',
  'templateId',
  ...CONVERSION_ATTRIBUTION_ACQUISITION_FIELDS,
] as const;

/**
 * The record. The acquisition fields are optional on input so a record that
 * predates them (an older app build's request, a device envelope stored
 * before the fields existed) still parses; every record minted by the
 * evaluators writes all seven, null when absent. A record must carry at least
 * one value, and a template id only beside the campaign it belongs to.
 */
export const conversionAttributionSchema = z
  .object({
    campaignId: conversionAttributionIdSchema.nullable(),
    templateId: conversionAttributionIdSchema.nullable(),
    utmSource: conversionAttributionUtmValueSchema.nullable().optional(),
    utmMedium: conversionAttributionUtmValueSchema.nullable().optional(),
    utmCampaign: conversionAttributionUtmValueSchema.nullable().optional(),
    utmContent: conversionAttributionUtmValueSchema.nullable().optional(),
    utmTerm: conversionAttributionUtmValueSchema.nullable().optional(),
    experimentArm: conversionAttributionExperimentArmSchema.nullable().optional(),
    linkRef: conversionAttributionIdSchema.nullable().optional(),
    source: conversionAttributionSourceSchema,
    // ISO-8601 text is transport only; the window is decided in Luxon.
    capturedAt: z.string().datetime({ offset: true }),
  })
  .strict()
  .superRefine((record, ctx) => {
    if (VALUE_FIELDS.every((field) => record[field] === null || record[field] === undefined)) {
      ctx.addIssue({ code: 'custom', message: 'An attribution record carries at least one value' });
    }
    if (record.templateId !== null && record.campaignId === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['templateId'],
        message: 'A template id is only meaningful beside its campaign id',
      });
    }
  });
export type ConversionAttribution = z.infer<typeof conversionAttributionSchema>;

/**
 * The ten attribution keys every designated conversion event carries, with
 * the schema each one is held to on the event payload and in the analytics
 * forwarder's registry. Nullable on purpose: null is the payload's own
 * "unattributed" value, which must lift as a value rather than be dropped.
 */
export const CONVERSION_ATTRIBUTION_EVENT_FIELD_SCHEMAS = {
  campaignId: conversionAttributionIdSchema.nullable(),
  templateId: conversionAttributionIdSchema.nullable(),
  attributionSource: conversionAttributionSourceSchema.nullable(),
  utmSource: conversionAttributionUtmValueSchema.nullable(),
  utmMedium: conversionAttributionUtmValueSchema.nullable(),
  utmCampaign: conversionAttributionUtmValueSchema.nullable(),
  utmContent: conversionAttributionUtmValueSchema.nullable(),
  utmTerm: conversionAttributionUtmValueSchema.nullable(),
  experimentArm: conversionAttributionExperimentArmSchema.nullable(),
  linkRef: conversionAttributionIdSchema.nullable(),
} as const;
export type ConversionAttributionEventFields = {
  [K in keyof typeof CONVERSION_ATTRIBUTION_EVENT_FIELD_SCHEMAS]: z.infer<
    (typeof CONVERSION_ATTRIBUTION_EVENT_FIELD_SCHEMAS)[K]
  >;
};
